/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { DateTime } from 'luxon';

import { ServiceType } from 'shared';

import { EntityId } from '../../types/api/EntityId';
import { ClusterBackupConfiguration } from '../../types/cluster/ClusterBackupConfiguration';
import { ClusterConfiguration } from '../../types/cluster/ClusterConfiguration';
import { ClusterShort } from '../../types/cluster/ClusterShort';
import { ImportDatasetMeta } from '../../types/miscellaneous/ImportDatasetMeta';
import { FormProps } from './components/forms/types';
import { WeekdayMap } from './CreateClusterOverlay.consts';

export enum CreateClusterOverlaySteps {
  STEP_INFO,
  STEP_SETTINGS,
  STEP_CONFIGURE_BACKUP,
  STEP_CONFIGURE_MAINTENANCE_WINDOWS,
  STEP_DATA_SETS,
  STEP_SUMMARY,
  STEP_BACKUP,
  STEP_SUPPORT_PLAN,
}

export type CreateClusterOverlayMapType<StepsEnum extends CreateClusterOverlaySteps> = {
  [K in StepsEnum]: {
    stepNumber: number | null;
    nextStep: CreateClusterOverlaySteps;
    prevStep: CreateClusterOverlaySteps;
  };
};

export type CreateClusterInfo = {
  name: string;
  description: string | null;
  organizationId: EntityId;
  projectId: EntityId;
  allowedSourceRanges: string[];
  allowedSourceRangesInput: string;
  allowAllConnections: boolean;
};

export type CreateClusterMiscellaneous = {
  allowedOrganizations?: EntityId[];
  sourceClusterBackupConfiguration?: ClusterBackupConfiguration;
};

export type CreateClusterSettings = {
  postgresVersion: number;
  replicas: number;
  cpuUnits: number;
  memoryGbs: number;
  storageGbs: number;
  storageClassId: string;
};

export type CreateClusterDataSets = {
  selectedDataSetIds: EntityId[];
  selectedDataSetCategoryId?: EntityId;
  dataSetsMetas: { [key: string]: ImportDatasetMeta | undefined };
};

export type CreateClusterConfigureBackup = {
  backupConfigurationLogicalEnabled: boolean;
  backupConfigurationLogicalSchedule: string;
  backupConfigurationLogicalDaysToRetain: string;
  backupConfigurationContinuousEnabled: boolean;
  backupConfigurationContinuousSchedule: string;
  backupConfigurationContinuousDaysToRetain: string;
};

export type CreateClusterConfigureMaintenanceWindows = {
  maintenanceWindows: {
    weekday: Weekday;
    startTimeUtc: DateTime;
    endTimeUtc: DateTime;
  }[];
};

export type CreateClusterBackupSettings = {
  backupOrganizationId?: EntityId;
  backupProjectId?: EntityId;
  backupClusterId?: EntityId;
  backupCluster: ClusterShort | null;
  backupClusterConfiguration?: ClusterConfiguration;
  backupTimestampMinDate: Date | null;
  backupTimestampMaxDate: Date | null;
  backupTimestamp: Date | null | undefined;
  cloneAcl: boolean;
  cloneParameters: boolean;
  cloneLogicalBackupConfiguration: boolean;
  cloneContinuousBackupConfiguration: boolean;
  oldestRestorePoint: string | null;
};

export type CreateClusterSupportPlan = {
  supportPlanId: EntityId;
};

export type CreateClusterValues = CreateClusterInfo &
  CreateClusterSettings &
  CreateClusterMiscellaneous &
  CreateClusterConfigureBackup &
  CreateClusterConfigureMaintenanceWindows &
  CreateClusterDataSets &
  CreateClusterSupportPlan;
export type CreateClusterFromBackupValues = CreateClusterBackupSettings & CreateClusterValues;

export type CreateClusterSettingsStep<Values extends CreateClusterValues & Partial<CreateClusterBackupSettings>> =
  FormProps<Values> & {
    postgresVersions: number[];
  };

export type OnClusterCreateCallback = (
  cluster: { id: EntityId },
  params?: {
    fromBackup: boolean;
    organizationId: EntityId;
    projectId: EntityId;
  },
) => any;

export type ServiceVO = { id: string; name: string; type: ServiceType };
export type ProjectVO = { id: string; name: string; services: Array<ServiceVO> };
export type OrganizationVO = { id: string; name: string; projects: Array<ProjectVO> };

export type SearchOption =
  | { group: 'organizations'; entity: { organization: OrganizationVO } }
  | { group: 'projects'; entity: { organization: OrganizationVO; project: ProjectVO } }
  | { group: 'services'; entity: { organization: OrganizationVO; project: ProjectVO; service: ServiceVO } };

export type FetchParamsState = {
  organizationId?: EntityId;
  projectId?: EntityId;
  clusterId?: EntityId;
};

export type Weekday = keyof typeof WeekdayMap;
